<template>
  <date-picker
    type="datetime"
    compact-time
    v-model="startDate"
    class="custom-date-picker"
    :format="'jYYYY-jMM-jDD hh:mm'"
    :displayFormat="'jYYYY-jMM-jDD hh:mm'"
    custom-input=".start-date-input"
  />
  <date-picker
    type="datetime"
    compact-time
    v-model="endDate"
    class="custom-date-picker"
    :format="'jYYYY-jMM-jDD hh:mm'"
    :displayFormat="'jYYYY-jMM-jDD hh:mm'"
    custom-input=".end-date-input"
  />
  <div
    class="flex flex-col items-center px-5 gap-4 max-h-[28rem] overflow-auto"
  >
    <div class="w-full flex flex-col gap-2">
      <label for="reminderTitle" class="w-full text-right mr-2"
        >تیتر رویداد</label
      >
      <input
        type="text"
        name="reminderTitle"
        class="input w-full"
        v-model="title"
      />
      <span v-if="errors.title" class="text-red-500 text-sm">{{
        errors.title
      }}</span>
    </div>

    <div class="w-full flex flex-col gap-2 mt-4 pb-4">
      <label for="eventColor" class="w-full text-right mr-2">رنگ رویداد</label>
      <div class="flex gap-4 text-stone-500">
        <label>
          <input
            type="radio"
            name="eventColor"
            value="danger"
            v-model="color"
          />
          قرمز
        </label>
        <label>
          <input
            type="radio"
            name="eventColor"
            value="success"
            v-model="color"
          />
          سبز
        </label>
        <label>
          <input
            type="radio"
            name="eventColor"
            value="primary"
            v-model="color"
          />
          آبی
        </label>
        <label>
          <input
            type="radio"
            name="eventColor"
            value="warning"
            v-model="color"
          />
          زرد
        </label>
      </div>
      <span v-if="errors.color" class="text-red-500 text-sm">{{
        errors.color
      }}</span>
    </div>

    <div class="w-full flex flex-col gap-2">
      <label for="startDate" class="w-full text-right mr-2"
        >تاریخ شروع رویداد</label
      >
      <input
        type="text"
        class="start-date-input input w-full font-vazir-thin"
        :value="startDate"
      />
      <span v-if="errors.startDate" class="text-red-500 text-sm">{{
        errors.startDate
      }}</span>
    </div>

    <div class="w-full flex flex-col gap-2">
      <label for="endDate" class="w-full text-right mr-2"
        >تاریخ پایان رویداد</label
      >
      <input
        type="text"
        class="end-date-input input w-full font-vazir-thin"
        :value="endDate"
      />
      <span v-if="errors.endDate" class="text-red-500 text-sm">{{
        errors.endDate
      }}</span>
    </div>

    <div class="mt-4 w-full flex justify-end items-center gap-4">
      <button
        @click="closeModal"
        class="text-stone-500 rounded-lg border border-transparent transition-all active:border-black py-2 px-4"
      >
        بستن
      </button>
      <button
        @click="handleSubmit"
        class="bg-blue-primary text-white rounded-lg py-2 px-4 transition-colors hover:bg-blue-primary-darker disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
        :disabled="mutation.isLoading"
      >
        افزودن رویداد
      </button>
    </div>
  </div>
</template>

<script setup>
import moment from "jalali-moment";
import { ref } from "vue";
import { defineEmits } from "vue";
import DatePicker from "vue3-persian-datetime-picker";
import { useMutation } from "@tanstack/vue-query"; // Import useMutation
import { postReminders } from "@/services/api/apiServiceReminders"; // Import postReminders
import { useStore } from "vuex"; // Import useStore for Vuex
import {
  addOneDay,
  convertToJalali,
  formatReminders,
} from "@/services/helpers";
import Swal from "sweetalert2";

const store = useStore(); // Access Vuex store

const initStartDate = convertToJalali(store.state.remindersSlice.selectedDate);
const initEndDate = addOneDay(initStartDate);

const title = ref("");
const color = ref("danger");
const startDate = ref(initStartDate);
const endDate = ref(initEndDate);

// Validation errors
const errors = ref({
  title: "",
  color: "",
  startDate: "",
  endDate: "",
});

const emit = defineEmits(["close", "submit"]);

// Mutation for posting reminder
const mutation = useMutation({
  mutationFn: postReminders,
  onSuccess: (data) => {
    const formattedData = formatReminders([data])[0];
    store.commit("remindersSlice/addReminder", formattedData);
    Swal.fire({
      icon: "success",
      title: "رویداد با موفقیت اضافه شد.",
      showConfirmButton: false,
      timer: 1500,
    });
    closeModal();
  },
  onError: (error) => {
    Swal.fire({
      icon: "error",
      title: "اضافه کردن رویداد جدید با خطا مواجه شد!",
      showConfirmButton: false,
      timer: 1500,
    });
    console.error("Error posting reminder:", error);
  },
});

function validateForm() {
  let isValid = true;

  // Reset errors
  errors.value = {
    title: "",
    color: "",
    startDate: "",
    endDate: "",
  };

  // Validate title
  if (!title.value.trim()) {
    errors.value.title = "تیتر رویداد اجباری است.";
    isValid = false;
  }

  // Validate color
  if (!color.value) {
    errors.value.color = "انتخاب رنگ رویداد اجباری است.";
    isValid = false;
  }

  // Validate start date
  if (!startDate.value) {
    errors.value.startDate = "تاریخ شروع رویداد اجباری است.";
    isValid = false;
  }

  // Validate end date
  if (!endDate.value) {
    errors.value.endDate = "تاریخ پایان رویداد اجباری است.";
    isValid = false;
  } else if (startDate.value && endDate.value) {
    // Ensure end date is after start date
    const start = moment(startDate.value, "jYYYY-jMM-jDD HH:mm");
    const end = moment(endDate.value, "jYYYY-jMM-jDD HH:mm");
    if (end.isBefore(start)) {
      errors.value.endDate = "تاریخ پایان باید بعد از تاریخ شروع باشد.";
      isValid = false;
    }
  }

  return isValid;
}

async function handleSubmit() {
  if (!validateForm()) {
    return;
  }

  const reminderObj = {
    title: title.value,
    start_date: formatDate(startDate.value),
    end_date: formatDate(endDate.value),
    user_id: store.state.userSlice.user.id,
  };

  // Call the mutation to post the reminder
  // mutation.mutate(reminderObj);
  emit("submit", reminderObj);
}

function formatDate(jalaliDate) {
  if (!jalaliDate) return null;
  return moment(jalaliDate, "jYYYY-jMM-jDD hh:mm").format(
    "YYYY-MM-DD hh:mm:ss",
  );
}

function closeModal() {
  emit("close");
}
</script>
